import axios from "axios";

import GenericPokkService, {
  throwNormalizedApiError,
} from "#/data-layer/GenericPokkService";
import env from "#/env";

/**
 * Service for interacting with the Keycloak Account API.
 *
 * Provides methods for retrieving information about the currently
 * authenticated user's credentials (password, OTP, WebAuthn, etc.).
 */
export default class KeycloakService extends GenericPokkService {
  constructor(params = {}) {
    super({
      baseUrl: env.OIDC_AUTHORITY,
      ...params,
    });
  }

  /**
   * Retrieves the credentials configured for the authenticated user.
   *
   * Calls the Keycloak Account API endpoint:
   * `GET /account/credentials`
   *
   * @param {string} accessToken Valid Keycloak access token.
   * @returns {Promise<Array<Object>>} List of user credentials (e.g. password, OTP, WebAuthn).
   * @throws {Object} Normalized API error returned by {@link throwNormalizedApiError}.
   */
  async read(accessToken) {
    const instance = axios.create({
      baseURL: this.baseUrl,
      connectTimeout: 180000,
      timeout: 360000,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
      },
      responseType: "json",
    });
    return instance
      .get(`/account/credentials`)
      .then(({ data }) => data)
      .catch(throwNormalizedApiError);
  }

  /**
   * Checks whether the given access token is still recognized by Keycloak.
   *
   * Calls the standard OIDC endpoint:
   * `GET /protocol/openid-connect/userinfo`
   *
   * Used to detect a Keycloak-side session end (e.g. the user logs out
   * directly in the Keycloak account console) that the local oidc-client-ts
   * user object doesn't otherwise know about.
   *
   * @param {string} accessToken Access token to validate.
   * @returns {Promise<boolean>} false if Keycloak reports the token/session
   * as no longer valid (401), true otherwise.
   */
  async isSessionValid(accessToken) {
    const instance = axios.create({
      baseURL: this.baseUrl,
      timeout: 10000,
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    });
    return instance
      .get(`/protocol/openid-connect/userinfo`)
      .then(() => true)
      .catch((error) => {
        if (error.response?.status === 401) return false;
        throw error;
      });
  }
}
